import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useHistory, useLocation } from 'react-router-dom';
import {
  AUTH_CHANGE_EVENT,
  clearUserSession,
  getUserAvatarUrl,
  getUserDisplayName,
  getUserInitials,
  getUserSession,
  canManageInventory,
  isSuperAdmin,
} from '../../services/authService';
import '../../styles/header.css';

const navItems = [
  { to: '/', label: 'Inicio', exact: true },
  { to: '/about', label: 'Acerca de nosotros' },
  { to: '/volunteer', label: 'Voluntariado' },
  { to: '/se-parte-de-nosotros', label: 'Sé parte de nosotros' },
  { to: '/donations', label: 'Donaciones' },
  { to: '/services', label: 'Servicios' },
  { to: '/subscriptions', label: 'Suscripciones' },
  { to: '/blog', label: 'Blog' },
];

const SearchIcon = () => (
  <svg className="blf-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3-3" />
  </svg>
);

const UserIcon = () => (
  <svg className="blf-icon blf-icon-user" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4 3.5-7 8-7s8 3 8 7" />
  </svg>
);

const CartIcon = () => (
  <svg className="blf-icon blf-icon-cart" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="9" cy="20" r="1" />
    <circle cx="17" cy="20" r="1" />
    <path d="M1 1h4l2.5 13h11l2-8H6" />
  </svg>
);

const CalendarIcon = () => (
  <svg className="blf-icon blf-icon-calendar" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M8 3v4M16 3v4M3 11h18" />
  </svg>
);

const BellIcon = () => (
  <svg className="blf-icon blf-icon-bell" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 8a6 6 0 10-12 0c0 7-3 7-3 7h18s-3 0-3-7" />
    <path d="M13.7 21a2 2 0 01-3.4 0" />
  </svg>
);

const Header = () => {
  const history = useHistory();
  const location = useLocation();
  const menuRef = useRef(null);
  const [sessionUser, setSessionUser] = useState(() => getUserSession());
  const [showUserMenu, setShowUserMenu] = useState(false);

  const displayName = getUserDisplayName(sessionUser);
  const avatarUrl = getUserAvatarUrl(sessionUser);
  const avatarInitials = getUserInitials(sessionUser);

  useEffect(() => {
    setSessionUser(getUserSession());
  }, [location.pathname]);

  useEffect(() => {
    const syncSession = () => {
      setSessionUser(getUserSession());
    };

    window.addEventListener(AUTH_CHANGE_EVENT, syncSession);
    return () => window.removeEventListener(AUTH_CHANGE_EVENT, syncSession);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    clearUserSession();
    setSessionUser(null);
    setShowUserMenu(false);
    history.push('/');
  };

  return (
    <header className="blf-header">
      <div className="blf-header-top">
        <div className="blf-header-top-inner">
          <Link to="/" className="blf-logo" aria-label="Beyond Limits Foundation">
            <img
              src={`${process.env.PUBLIC_URL}/images/logo-blf.png`}
              alt="Beyond Limits Foundation"
              className="blf-logo-img"
            />
          </Link>

          <div className="blf-search-wrapper">
            <SearchIcon />
            <input
              className="blf-search-input"
              type="text"
              placeholder="Buscar capacitación o terapia..."
            />
          </div>

          <div className="blf-header-actions">
            {sessionUser ? (
              <div className="blf-header-user">
                <Link to="/agenda" className="blf-btn blf-btn-agenda">
                  <CalendarIcon />
                  Agenda
                </Link>

                <div className="blf-user-menu-wrap" ref={menuRef}>
                  <button
                    type="button"
                    className="blf-user-profile"
                    onClick={() => setShowUserMenu((value) => !value)}
                    aria-expanded={showUserMenu}
                    aria-haspopup="true"
                  >
                    <span
                      className="blf-user-avatar"
                      style={avatarUrl ? { backgroundImage: `url(${avatarUrl})` } : undefined}
                    >
                      {!avatarUrl ? avatarInitials : null}
                    </span>
                    <span className="blf-user-name">{displayName}</span>
                  </button>

                  {showUserMenu ? (
                    <div className="blf-user-dropdown" role="menu">
                      {isSuperAdmin(sessionUser) ? (
                        <Link
                          to="/admin/usuarios"
                          className="blf-user-dropdown-item blf-user-dropdown-link"
                          onClick={() => setShowUserMenu(false)}
                        >
                          Administración
                        </Link>
                      ) : null}
                      {isSuperAdmin(sessionUser) ? (
                        <Link
                          to="/admin/nomina"
                          className="blf-user-dropdown-item blf-user-dropdown-link"
                          onClick={() => setShowUserMenu(false)}
                        >
                          Nómina
                        </Link>
                      ) : null}
                      {canManageInventory(sessionUser) ? (
                        <Link
                          to="/admin/inventario"
                          className="blf-user-dropdown-item blf-user-dropdown-link"
                          onClick={() => setShowUserMenu(false)}
                        >
                          Inventario
                        </Link>
                      ) : null}
                      <Link
                        to="/perfil"
                        className="blf-user-dropdown-item blf-user-dropdown-link"
                        onClick={() => setShowUserMenu(false)}
                      >
                        Mi perfil
                      </Link>
                      {isSuperAdmin(sessionUser) ? (
                        <Link
                          to="/admin/configuracion"
                          className="blf-user-dropdown-item blf-user-dropdown-link"
                          onClick={() => setShowUserMenu(false)}
                        >
                          Configuración
                        </Link>
                      ) : null}
                      <button type="button" className="blf-user-dropdown-item" onClick={handleLogout}>
                        Cerrar sesión
                      </button>
                    </div>
                  ) : null}
                </div>

                <button type="button" className="blf-notification-btn" aria-label="Notificaciones">
                  <BellIcon />
                  <span className="blf-notification-badge" />
                </button>
              </div>
            ) : (
              <>
                <Link to="/registro/crear-cuenta" className="blf-btn blf-btn-primary">
                  Regístrate
                </Link>
                <Link to="/iniciar-sesion" className="blf-btn blf-btn-outline">
                  <UserIcon />
                  Iniciar Sesión
                </Link>
              </>
            )}

            <button type="button" className="blf-cart-btn" aria-label="Carrito">
              <CartIcon />
              <span className="blf-cart-badge" />
            </button>
          </div>
        </div>
      </div>

      <div className="blf-header-bottom">
        <div className="blf-header-bottom-inner">
          <nav className="blf-nav">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                exact={item.exact}
                className="blf-nav-link"
                activeClassName="blf-nav-link-active"
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="blf-language-switcher">
            <span>Cambiar idioma</span>
            <span className="blf-language-arrow">▼</span>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
